import React, { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";

interface IInputProps {
	label: string;
	type: string;
	value?: string;
	onChange: (value: string, isValid?: boolean) => void;
	required?: boolean;
	disabled?: boolean;
	readOnly?: boolean;
	noCross?: boolean;
	validations?: ((value: string) => string | null)[]; // Array of custom validation functions
	useBuiltInValidations?: boolean; // Enable built-in common validations
	validateOnBlur?: boolean; // Trigger validations on blur
	helpText?: string;
	maxLength?: number;
}

const InputBox: React.FC<IInputProps> = ({
	label,
	type,
	value = "",
	onChange,
	required = false,
	disabled = false,
	readOnly = false,
	noCross = false,
	validations = [],
	useBuiltInValidations = true,
	validateOnBlur = true,
	helpText,
	maxLength,
}) => {
	const [showPassword, setShowPassword] = useState(false);
	const [validationError, setValidationError] = useState<string | null>(null);
	const [touched, setTouched] = useState(false);
	const togglePasswordVisibility = () => setShowPassword(!showPassword);

	// Built-in validations
	const builtInValidations: ((value: string) => string | null)[] = [];
	if (useBuiltInValidations) {
		if (required) {
			builtInValidations.push((value) =>
				value.trim() === "" ? `${label} is required` : null
			);
		}

		if (type === "email") {
			builtInValidations.push((value) =>
				/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
					? null
					: "Please enter a valid email"
			);
		}

		if (type === "tel") {
			builtInValidations.push((value) =>
				/^\+?[0-9]{7,15}$/.test(value)
					? null
					: "Please enter a valid phone number"
			);
		}

		if (type === "url") {
			builtInValidations.push((value) =>
				/^(https?:\/\/)?(([\w-]+\.)+[\w-]{2,}|localhost)(:\d{1,5})?(\/\S*)?$/.test(
					value
				)
					? null
					: "Please enter a valid URL"
			);
		}
	}

	// Validation Logic
	const runValidations = (currentValue: string): boolean => {
		for (const validate of [...builtInValidations, ...validations]) {
			const error = validate(currentValue);
			if (error) {
				setValidationError(error);
				return false;
			}
		}
		setValidationError(null);
		return true;
	};

	const handleBlur = () => {
		setTouched(true);
		const isValid = runValidations(value);
		onChange(value, isValid);
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		const isValid = runValidations(newValue);
		onChange(newValue, isValid);
		if (!validateOnBlur) {
			runValidations(newValue);
		}
	};

	return (
		<div
			className={`relative z-0 w-full flex flex-col items-start justify-center  ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
		>
			<div className="w-full">
				<div className="relative w-full">
					<input
						type={
							type === "password" && !showPassword
								? "password"
								: "text"
						}
						id="floating_standard"
						value={value}
						onChange={handleChange}
						onBlur={handleBlur}
						className={`block py-2.5 w-full ${type === "number" ? "font-code" : "font-Poppins"}  tracking-wider font-normal text-md text-gray-900 bg-transparent border-0 border-b-2 border-gray-400 appearance-none focus:outline-none focus:ring-0 focus:border-sky-600 peer`}
						placeholder=""
						disabled={disabled}
						readOnly={readOnly}
						maxLength={maxLength}
					/>
					{helpText && (
						<div className="w-full flex items-center justify-start pt-1">
							<span className="font-Poppins font-semibold tracking-wider text-xs text-sky-500">
								{helpText}
							</span>
						</div>
					)}
					<label
						htmlFor="floating_standard"
						className={`absolute font-Poppins font-medium text-md text-gray-900 duration-300 transform scale-75 -translate-y-6 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-sky-900 ${
							value
								? "scale-75 -translate-y-6 left-0 text-gray-800 font-Poppins tracking-wider"
								: "peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 left-0"
						}`}
					>
						{label}
						{required && (
							<span className="font-Poppins text-lg text-red-500">
								{" "}
								*
							</span>
						)}
					</label>
				</div>
				{/* Label */}

				{/* Clear Icon */}
				{!readOnly && !disabled && value && !noCross && (
					<button
						type="button"
						className="absolute right-2 -top-2 h-full flex items-center cursor-pointer"
						onClick={() => onChange("")} // Clear the input value
						disabled={disabled}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="currentColor"
							className="w-5 h-5 text-gray-500"
						>
							<path
								fillRule="evenodd"
								d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
								clipRule="evenodd"
							/>
						</svg>
					</button>
				)}

				{/* Password Visibility Toggle */}
				{type === "password" && value && (
					<span
						className="absolute right-10 top-0 h-full flex items-center cursor-pointer"
						onClick={togglePasswordVisibility}
					>
						{showPassword ? (
							<EyeSlashIcon className="w-5 h-5 text-gray-500" />
						) : (
							<EyeIcon className="w-5 h-5 text-gray-500" />
						)}
					</span>
				)}
			</div>

			{/* Validation Error */}
			{touched && validationError && (
				<div className="w-full flex items-center justify-start pt-1">
					<span className="font-semibold text-xs text-red-500">
						{validationError}
					</span>
				</div>
			)}
		</div>
	);
};

export default InputBox;
